import { LogOut, ShieldCheck } from "lucide-react";

import { useAuth } from "../auth/AuthContext";
import { CHAPTER } from "../chapter";
import type { NavId } from "../components/Shell";
import { NAV_LABEL, roleDisplayLabel } from "../lib/labels";

/** What an organiser can do, in one sentence. */
const ORGANISER_ACCESS =
  "You can see and change everything, including who has access.";

/** What a team member can do, in one sentence. Since #117
 *  guest details are organisers only, and this page is often the only
 *  one they can open, so it says why. */
const TEAM_ACCESS =
  "You can sign in, but guest details are only open to organisers. Ask an organiser if you need more.";

/** Your account: who is signed in, what their access allows, and the way
 *  out. Organisers also get a way to Team access. */
export function Settings({ onNavigate }: { onNavigate: (id: NavId) => void }) {
  const { user, role, signOut } = useAuth();
  const isOrganiser = role === "superadmin";
  const canOpenTeamAccess = isOrganiser && CHAPTER.features.includes("roles");

  return (
    <section className="mx-auto max-w-3xl px-5 py-8 md:px-12 md:py-12">
      <h1 className="page-title">{NAV_LABEL.settings}</h1>

      <div className="surface mt-6 p-5 md:p-6">
        <p className="text-[20px] leading-snug font-bold break-words text-[color:var(--color-ink)]">
          {user?.fullName ?? "No name on your account"}
        </p>
        {user?.email ? (
          <p className="mt-1 text-[16px] break-words text-[color:var(--color-ink-soft)]">
            {user.email}
          </p>
        ) : null}
        <p className="mt-5 text-[16px] text-[color:var(--color-ink)]">
          Your access:{" "}
          <strong className="font-semibold">
            {roleDisplayLabel(role, CHAPTER.id)}
          </strong>
        </p>
        <p className="mt-1 text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
          {isOrganiser ? ORGANISER_ACCESS : TEAM_ACCESS}
        </p>
      </div>

      {canOpenTeamAccess ? (
        <div className="surface mt-4 flex flex-col items-start gap-3 p-5 md:p-6">
          <p className="text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
            Change who can sign in on the {NAV_LABEL.roles} page.
          </p>
          <button
            type="button"
            onClick={() => onNavigate("roles")}
            className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-full bg-[color:var(--color-ink)] px-5 text-[15px] font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-85"
          >
            <ShieldCheck size={16} aria-hidden />
            Open {NAV_LABEL.roles}
          </button>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => {
          void signOut();
        }}
        className="mt-6 inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-full border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-5 text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
      >
        <LogOut size={16} aria-hidden />
        Sign out
      </button>
    </section>
  );
}
